import { useState, useCallback } from "react";
import { Contact } from "../../../common/ContactForm";
import { Country } from "../../../../services/locationService";
import { LocationData } from "../../../common/GooglePlacesAutocomplete";
import { AddressFormData } from "./types";
import { createEmptyForm, isValidPhoneNumber } from "./utils";

// Postal code error state interface
export interface PostalCodeErrors {
  source?: string | null;
  destination?: string | null;
}

// Address validation hook
export const useAddressValidation = (
  allowedOriginCountryId: number | null,
  allowedDestinationCountryId: number | null,
  allCountries: Country[] = [],
  postalCodeErrors?: PostalCodeErrors
) => {
  const isFormValid = useCallback(
    (form: AddressFormData, isDestination: boolean = false): boolean => {
      const hasCity = Boolean(form.city_other && form.city_other.trim().length > 0);

      const basicValid = Boolean(
        form.contactName &&
        form.street &&
        form.zipCode &&
        form.country_id &&
        hasCity
      );

      if (!basicValid) return false;

      // Check for postal code validation errors
      if (postalCodeErrors) {
        const postalError = isDestination
          ? postalCodeErrors.destination
          : postalCodeErrors.source;
        if (postalError) {
          return false;
        }
      }

      // Phone validation - if phone is provided, it must be valid
      if (form.phone && form.phone.trim() !== '') {
        const selectedCountry = allCountries.find(c => c.id === form.country_id);
        const countryCode = selectedCountry?.iso2;
        const phoneValid = isValidPhoneNumber(form.phone, countryCode);

        if (!phoneValid) {
          return false;
        }
      }

      return true;
    },
    [allCountries, postalCodeErrors]
  );

  const isContactValidForSource = useCallback(
    (contact: Contact): boolean => {
      if (!allowedOriginCountryId) return true;
      return contact.country_id === allowedOriginCountryId;
    },
    [allowedOriginCountryId]
  );

  const isContactValidForDestination = useCallback(
    (contact: Contact): boolean => {
      if (!allowedDestinationCountryId) return true;
      return contact.country_id === allowedDestinationCountryId;
    },
    [allowedDestinationCountryId]
  );

  return {
    isFormValid,
    isContactValidForSource,
    isContactValidForDestination,
  };
};

// Form handlers hook
export const useFormHandlers = () => {
  const [sourceForm, setSourceForm] =
    useState<AddressFormData>(createEmptyForm);
  const [destinationForm, setDestinationForm] =
    useState<AddressFormData>(createEmptyForm);

  const handleSourceFormChange = useCallback(
    (field: keyof AddressFormData, value: string | number | null) => {
      setSourceForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleDestinationFormChange = useCallback(
    (field: keyof AddressFormData, value: string | number | null) => {
      setDestinationForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSourceGooglePlaceSelect = useCallback(
    (locationData: LocationData) => {
      setSourceForm((prev) => ({
        ...prev,
        street: locationData.street || prev.street,
        country_id: locationData.country_id || prev.country_id,
        state_id: locationData.state_id || prev.state_id,
        city_id: locationData.city_id || prev.city_id,
        city_other: locationData.city_other || prev.city_other,
      }));
    },
    []
  );

  const handleDestinationGooglePlaceSelect = useCallback(
    (locationData: LocationData) => {
      setDestinationForm((prev) => ({
        ...prev,
        street: locationData.street || prev.street,
        country_id: locationData.country_id || prev.country_id,
        state_id: locationData.state_id || prev.state_id,
        city_id: locationData.city_id || prev.city_id,
        city_other: locationData.city_other || prev.city_other,
      }));
    },
    []
  );

  return {
    sourceForm,
    destinationForm,
    setSourceForm,
    setDestinationForm,
    handleSourceFormChange,
    handleDestinationFormChange,
    handleSourceGooglePlaceSelect,
    handleDestinationGooglePlaceSelect,
  };
};
